import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

interface Invoice {
  id: number
  invoice_number: string
  invoice_pdf_url: string | null
  sent_to_accounting: boolean
}

interface Props {
  [key: string]: unknown
  invoice: Invoice
}

export default function InvoiceQuickEdit() {
  const { invoice } = usePage<Props>().props

  const { data, setData, patch, processing, errors } = useForm({
    invoice_pdf_url: invoice.invoice_pdf_url || "",
    sent_to_accounting: invoice.sent_to_accounting,
  })

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/invoices/${invoice.id}/quick_update`)
  }

  return (
    <>
      <Head title={`Quick Edit - ${invoice.invoice_number}`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/invoices">Invoices</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={`/invoices/${invoice.id}`}>{invoice.invoice_number}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Quick Edit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={`/invoices/${invoice.id}`}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">Quick Edit - {invoice.invoice_number}</h1>
              <p className="text-muted-foreground text-sm">Update PDF link and accounting status</p>
            </div>
          </div>
        </div>

        <div className="max-w-lg">
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader>
                <CardTitle>Quick Edit</CardTitle>
              </CardHeader>
              <CardContent>
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="invoice_pdf_url">Invoice PDF Link (Google Drive)</Label>
                    <Input
                      id="invoice_pdf_url"
                      type="url"
                      value={data.invoice_pdf_url}
                      onChange={(e) => setData("invoice_pdf_url", e.target.value)}
                      placeholder="https://drive.google.com/file/d/..."
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="sent_to_accounting"
                      checked={data.sent_to_accounting}
                      onCheckedChange={(checked) => setData("sent_to_accounting", checked === true)}
                    />
                    <Label htmlFor="sent_to_accounting" className="font-normal">
                      Sent to Accounting
                    </Label>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="justify-end gap-2 border-t">
                <Button variant="ghost" asChild>
                  <a href={`/invoices/${invoice.id}`}>Cancel</a>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Updating..." : "Update"}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </div>
      </div>
    </>
  )
}

InvoiceQuickEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
